import React, { useEffect, useRef, useState } from 'react';
import { Pause, Play, Volume1, Volume2, VolumeX } from 'lucide-react';

const AudioPrayer = ({ guide, day }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentVolume, setCurrentVolume] = useState(0.7);
    const [isMuted, setIsMuted] = useState(false);
    const utteranceRef = useRef(null);
    const synthRef = useRef(window.speechSynthesis);

    const dayData = guide?.days?.find((item) => item.day === day || item.dayNumber === day);
    const audioText = dayData ? `
        Day ${day} of ${guide.title || 'your personal prayer guide'}.
        Today's scripture is ${dayData.scripture}.
        Let us pray. ${dayData.prayer}
        Take a quiet moment for reflection. Amen.
    ` : '';

    useEffect(() => {
        const synth = synthRef.current;
        return () => {
            synth.cancel();
        };
    }, [day]);

    const beginSpeech = () => {
        if (!audioText) return;

        const synth = synthRef.current;
        synth.cancel();
        const utterance = new SpeechSynthesisUtterance(audioText);
        utterance.rate = 0.88;
        utterance.pitch = 1;
        utterance.volume = isMuted ? 0 : currentVolume;
        utterance.onend = () => setIsPlaying(false);
        utterance.onerror = () => setIsPlaying(false);
        utteranceRef.current = utterance;
        synth.speak(utterance);
        setIsPlaying(true);
    };

    const togglePlay = () => {
        const synth = synthRef.current;
        if (isPlaying) {
            synth.pause();
            setIsPlaying(false);
        } else if (synth.paused) {
            synth.resume();
            setIsPlaying(true);
        } else {
            beginSpeech();
        }
    };

    const toggleMute = () => {
        setIsMuted((muted) => !muted);
        if (synthRef.current.speaking) {
            synthRef.current.cancel();
            setIsPlaying(false);
        }
    };

    const handleVolumeChange = (event) => {
        const volume = Number.parseFloat(event.target.value);
        setCurrentVolume(volume);
        setIsMuted(volume === 0);
    };

    if (!dayData) return null;

    return (
        <div className="guide-audio-player">
            <button
                type="button"
                onClick={togglePlay}
                className="guide-audio-play"
                aria-label={isPlaying ? 'Pause guided prayer' : 'Play guided prayer'}
            >
                {isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" />}
            </button>

            <div className="guide-audio-status">
                <small>{isPlaying ? 'Guided prayer playing' : 'Guided prayer audio'}</small>
                <strong>Day {day}: {dayData.theme}</strong>
            </div>

            <button type="button" onClick={toggleMute} className="guide-audio-volume-button" aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}>
                {isMuted ? <VolumeX size={18} /> : currentVolume < 0.5 ? <Volume1 size={18} /> : <Volume2 size={18} />}
            </button>
            <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={isMuted ? 0 : currentVolume}
                onChange={handleVolumeChange}
                aria-label="Guided prayer volume"
            />
        </div>
    );
};

export default AudioPrayer;
